import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import {
    CourseSubjectDetailState,
    CourseSubjectState,
    SelectedPurchaseSubjectState,
    SubjectState,
    UserSubjectState,
} from '../types'

interface SubjectLink {
    title: string
    lessonLink: string
    lessonComment: string
}

interface CourseDetailParams {
    courseSubjectIds: number[]
    coursePriceId: number
}

export const courseSubjectApiSlice = createApi({
    reducerPath: 'courseSubjectApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: [
        'ActiveCourseSubject',
        'InactiveCourseSubject',
        'Subject',
        'SubjectLink',
        'CourseEnrollSubject'
    ],
    endpoints: (build) => ({
        fetchActiveCourseSubjects: build.query<CourseSubjectState[], number>({
            query: (id) => ({
                url: `course-subject/active/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'ActiveCourseSubject' as const,
                            id,
                        })),
                        { type: 'ActiveCourseSubject', id: 'LIST' },
                    ]
                    : [{ type: 'ActiveCourseSubject', id: 'LIST' }],
            transformResponse: (response: any) => {
                if (response.data.length > 0) {
                    const result = response.data.map(
                        (courseSubject: CourseSubjectState) => ({
                            ...courseSubject,
                            titleKz: courseSubject.subject.titleKz,
                            titleRu: courseSubject.subject.titleRu,
                        }),
                    )

                    return result
                }

                return response.data
            },
        }),

        fetchCuratorCourseSubjects: build.query<CourseSubjectState[], number>({
            query: (id) => ({
                url: `course-subject/curator/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'ActiveCourseSubject' as const,
                            id,
                        })),
                        { type: 'ActiveCourseSubject', id: 'LIST' },
                    ]
                    : [{ type: 'ActiveCourseSubject', id: 'LIST' }],
            transformResponse: (response: any) => {
                if (response.data.length > 0) {
                    return response.data.map((courseSubject: CourseSubjectState) => ({
                        ...courseSubject,
                        titleKz: courseSubject.subject.titleKz,
                        titleRu: courseSubject.subject.titleRu,
                    }))
                }

                return response.data
            },
        }),

        fetchInactiveCourseSubjects: build.query<SubjectState[], number>({
            query: (id) => ({
                url: `course-subject/inactive/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'InactiveCourseSubject' as const,
                            id,
                        })),
                        { type: 'InactiveCourseSubject', id: 'LIST' },
                    ]
                    : [{ type: 'InactiveCourseSubject', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        createCourseSubject: build.mutation<any, any>({
            query: (courseSubject) => ({
                url: 'course-subject',
                method: 'POST',
                body: courseSubject,
            }),
            invalidatesTags: [
                { type: 'ActiveCourseSubject', id: 'LIST' },
                { type: 'InactiveCourseSubject', id: 'LIST' },
            ],
        }),

        updateCourseSubjectLanguage: build.mutation<void, any>({
            query: (courseSubject) => ({
                url: `course-subject/${courseSubject.courseSubjectId}`,
                method: 'PUT',
                body: courseSubject,
            }),
            invalidatesTags: [{ type: 'ActiveCourseSubject', id: 'LIST' }],
        }),

        updateCourseSubjectRequired: build.mutation<void, { courseSubjectId: number, isRequired: boolean }>({
            query: ({ courseSubjectId, isRequired }) => ({
                url: `course-subject/${courseSubjectId}/required`,
                method: 'PUT',
                body: {
                    isRequired
                },
            }),
            invalidatesTags: [{ type: 'ActiveCourseSubject', id: 'LIST' }],
        }),

        deleteCourseSubject: build.mutation<void, number>({
            query: (id) => ({
                url: `/course-subject/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: 'ActiveCourseSubject', id: 'LIST' },
                { type: 'InactiveCourseSubject', id: 'LIST' },
            ],
        }),

        getSubjectByCourseSubjectIds: build.mutation<
            Array<CourseSubjectDetailState>,
            Array<number>
        >({
            query: (ids) => ({
                url: `/course-subject/get-by-ids`,
                method: 'POST',
                body: ids,
            }),
            transformResponse: (response: any) => response.data,
        }),

        fetchAllStudentSubjects: build.query<UserSubjectState[], number>({
            query: (userCourseEnrollId) => ({
                url: `user-subject/my-course-subjects/${userCourseEnrollId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ courseSubjectId }) => ({
                            type: 'Subject' as const,
                            courseSubjectId,
                        })),
                        { type: 'Subject', id: 'LIST' },
                    ]
                    : [{ type: 'Subject', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        fetchAllTeacherSubjects: build.query<UserSubjectState[], void>({
            query: () => ({
                url: `user-subject/teacher/course-subjects`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ courseSubjectId }) => ({
                            type: 'Subject' as const,
                            courseSubjectId,
                        })),
                        { type: 'Subject', id: 'LIST' },
                    ]
                    : [{ type: 'Subject', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getSubjectLinks: build.query<SubjectLink[], number>({
            query: (courseSubjectId) => ({
                url: `user-subject/teacher/lesson-data/${courseSubjectId}`,
            }),
            providesTags: ['SubjectLink'],
            transformResponse: (response: any) => response.data,
        }),

        getCourseSubjectsPurchaseOptions: build.mutation<
            SelectedPurchaseSubjectState[],
            CourseDetailParams
        >({
            query: (courseSubjectIds) => ({
                url: 'purchase/course-subject/get-by-ids',
                method: 'POST',
                body: courseSubjectIds,
            }),
            transformResponse: (response: any) => response.data,
        }),

        fetchCourseSubjectsByUserCourseEnrollId: build.query<CourseSubjectState[], number>({
            query: (userCourseEnrollId) => ({
                url: `course-subject/${userCourseEnrollId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'CourseEnrollSubject' as const,
                            id,
                        })),
                        { type: 'CourseEnrollSubject', id: 'LIST' },
                    ]
                    : [{ type: 'CourseEnrollSubject', id: 'LIST' }],
            transformResponse: (response: any) => response?.data,
        }),
        updateCourseSubject: build.mutation<void, { courseSubjectId: number, courseSubject: CourseSubjectState | FormData }>({
            query: ({ courseSubjectId, courseSubject }) => ({
                url: `course-subject/update/${courseSubjectId}`,
                method: 'PUT',
                body: courseSubject,
            }),
            invalidatesTags: [{ type: 'ActiveCourseSubject', id: 'LIST' }],
        }),
    }),
})
